/**
 * ログイン状態（distinctId）の遷移から、PostHog に対して行うべき
 * identify / reset 操作を決定する純関数。
 *
 * identify / reset の分岐（ログイン / ログアウト / ユーザー切替）は本来
 * {@link PostHogProvider} の useEffect 内にあるが、React のレンダリングなしで
 * 単体テストできるよう、判定ロジックだけをここに切り出している。
 */

/** distinctId 遷移に対して PostHog へ行う操作。 */
type IdentityAction =
  | { type: 'identify'; distinctId: string }
  | { type: 'resetThenIdentify'; distinctId: string }
  | { type: 'reset' }
  | { type: 'none' };

/**
 * 直前と最新の distinctId から、実行すべき identify / reset 操作を求める。
 *
 * @param previousDistinctId 直前にセットされていた distinctId（未ログインは `null`）
 * @param nextDistinctId 最新の distinctId（ログアウト中は `null` / `undefined`）
 */
export const resolveIdentityTransition = (
  previousDistinctId: string | null,
  nextDistinctId: string | null | undefined,
): IdentityAction => {
  const hasLoggedIn = Boolean(nextDistinctId) && nextDistinctId !== previousDistinctId;
  const hasLoggedOut = !nextDistinctId && Boolean(previousDistinctId);

  if (hasLoggedIn && nextDistinctId) {
    // ログアウトを挟まないユーザー切替（別 distinctId へ直接遷移）では、
    // 前ユーザーの匿名状態が新ユーザーに混ざらないよう先に reset してから identify する。
    return previousDistinctId
      ? { type: 'resetThenIdentify', distinctId: nextDistinctId }
      : { type: 'identify', distinctId: nextDistinctId };
  }
  if (hasLoggedOut) {
    // 共有PCで次ユーザーが前 distinctId を継承しないよう reset する。
    return { type: 'reset' };
  }
  return { type: 'none' };
};
